<script setup lang="ts">
import { UiButton } from "@packages/component-library";
import { useWindowSize } from "@vueuse/core";
import { useUserStore } from "@/modules/user-module";
import { useI18n } from "vue-i18n";

defineProps({
  description: {
    type: String,
    default: "",
  },
});
const emit = defineEmits(["close", "initRef"]);
const { width } = useWindowSize();
const userStore = useUserStore();
const { t } = useI18n();
</script>

<template>
  <Teleport to="body">
    <div
      :ref="
        (e) => {
          emit('initRef', e);
        }
      "
      class="auth-modal"
    >
      <div class="auth-modal__wrapper">
        <div class="auth-modal__info">
          <h5>{{ t("authModal.title") }}</h5>

          <p>
            {{ description }}
          </p>

          <div v-if="width > 1239" class="auth-modal__actions">
            <UiButton
              :label="t('system.auth')"
              size="l"
              @click="userStore.authUser()"
            />
            <UiButton
              :label="t('system.cancel')"
              size="l"
              color="secondary"
              @click="emit('close')"
            />
          </div>
        </div>

        <div class="auth-modal__image">
          <img src="@/assets/images/auth-image.svg" alt="" />
        </div>
      </div>

      <div v-if="width < 1240" class="auth-modal__actions">
        <UiButton
          :label="t('system.auth')"
          size="m"
          @click="userStore.authUser()"
        />
        <UiButton
          :label="t('system.cancel')"
          size="m"
          color="secondary"
          @click="emit('close')"
        />
      </div>
    </div>
  </Teleport>
</template>

<style scoped lang="scss">
.auth-modal {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: 0;
  z-index: 10000;
  background-color: var(--color-semantic-background-normal-bg-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-flow: column;
  gap: calc($base-space * 8);
  overflow: auto;

  @media (max-width: 1239px) {
    padding: 0 calc($base-space * 4);
  }

  @media (max-width: 1023px) {
    padding: calc($base-space * 6) calc($base-space * 4);
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: 1fr auto;
    justify-items: center;
    gap: calc($base-space * 6);
  }

  &__wrapper {
    width: 820px;
    display: grid;
    grid-template-columns: 484px auto;
    gap: calc($base-space * 4);
    align-items: center;

    @media (max-width: 1239px) {
      width: auto;
      max-width: 400px;
      display: flex;
      flex-flow: column-reverse;
      gap: calc($base-space * 4);
      justify-content: center;
    }
  }

  &__info {
    > h5 {
      @include h3();
      color: var(--color-semantic-content-normal-primary);

      @media (max-width: 1239px) {
        text-align: center;
      }
    }

    > p {
      margin: calc($base-space * 2) 0 0;
      @include body-m();
      color: var(--color-semantic-content-normal-secondary);

      @media (max-width: 1239px) {
        text-align: center;
      }

      > span {
        color: var(--color-semantic-content-normal-link);
      }
    }
  }

  &__image {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-flow: column;
    gap: calc($base-space * 4);

    padding: 0 ($base-space * 12.5);

    @media (max-width: 1239px) {
      padding: ($base-space * 5) 0;
    }
  }

  &__actions {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: calc($base-space * 3);
    margin: calc($base-space * 8) 0 0;

    @media (max-width: 1239px) {
      margin: 0;
      width: 400px;
      flex-flow: column;
      gap: calc($base-space * 2);

      > button {
        width: 100%;
      }
    }

    @media (max-width: 1023px) {
      width: 100%;
      > button {
        width: 100%;
      }
    }
  }
}
</style>
